import styles from 'css/src/components/public-pages/contentful-components/FAQComponent.module.scss';

import React, { useState } from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Document } from '@contentful/rich-text-types';

import { type DeepReadonly, toMutableCopy } from 'js/src/libs/consts';

type FAQItem = {
    question: string;
    answer: Document;
};

type FAQData = Record<string, string | Document | undefined>;

const FAQComponent = React.memo(function FAQComponent({
    data,
    bgColor,
    lazyLoad,
}: {
    data: DeepReadonly<FAQData>;
    bgColor: string;
    lazyLoad: boolean;
}) {
    // Build FAQ items array from the data by dynamically finding all question/answer pairs
    // Iterate through question numbers until we find no more questions
    const faqItems: FAQItem[] = [];
    let i = 1;
    const maxQuestions = 100; // Reasonable upper limit to prevent infinite loops

    while (i <= maxQuestions) {
        const question = data[`question_${i}`];
        const answer = data[`answer_${i}`];

        if (typeof question === 'string' && question && typeof answer === 'object' && answer) {
            faqItems.push({ question, answer: toMutableCopy<Document>(answer) });
            i++;
        } else {
            // Stop when we find a missing question (assumes sequential numbering)
            break;
        }
    }

    if (faqItems.length === 0) {
        return null;
    }

    return (
        <div
            className={`contentful-component ${styles['faq']}`}
            style={{ backgroundColor: bgColor }}
        >
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-lg-8 col-md-10">
                        <div className={styles['faq-list']}>
                            {faqItems.map((item, index) => (
                                <FAQItem
                                    key={index}
                                    question={item.question}
                                    answer={item.answer}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

const FAQItem = React.memo(function FAQItem({
    question,
    answer,
}: {
    question: string;
    answer: Document;
}) {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div className={styles['faq-item']}>
            <button
                className={`${styles['faq-question']} btn btn-link`}
                onClick={() => setIsExpanded(!isExpanded)}
                type="button"
                aria-expanded={isExpanded}
            >
                <span className={styles['faq-question-text']}>{question}</span>
                <span className={`${styles['faq-icon']} ${isExpanded ? styles['expanded'] : ''}`}>
                    +
                </span>
            </button>
            <div className={`collapse ${isExpanded ? 'show' : ''}`}>
                <div className={styles['faq-answer']}>{documentToReactComponents(answer)}</div>
            </div>
        </div>
    );
});

export default FAQComponent;
